/**
 * Rate Limiter for Nominatim API
 *
 * Implements proper rate limiting to comply with Nominatim usage policy
 * of maximum 1 request per second.
 */

/**
 * Simple rate limiter implementation
 */
class RateLimiter {
  private queue: Array<() => void> = [];
  private processing = false;
  private lastRequestTime = 0;

  constructor(private readonly minIntervalMs: number) {}

  /**
   * Execute a function with rate limiting
   * @param fn Function to execute
   * @returns Promise that resolves when function completes
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const result = await fn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });

      this.processQueue();
    });
  }

  /**
   * Process the queue of pending requests
   */
  private async processQueue(): Promise<void> {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;

      // Wait if we need to respect rate limit
      if (timeSinceLastRequest < this.minIntervalMs) {
        const waitTime = this.minIntervalMs - timeSinceLastRequest;
        await this.sleep(waitTime);
      }

      const nextFunction = this.queue.shift();
      if (nextFunction) {
        this.lastRequestTime = Date.now();
        await nextFunction();
      }
    }

    this.processing = false;
  }

  /**
   * Sleep for specified number of milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get the number of pending requests
   */
  getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Clear all pending requests
   */
  clearQueue(): void {
    this.queue.length = 0;
  }

  /**
   * Get time until next request can be made
   */
  getTimeUntilNextRequest(): number {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    return Math.max(0, this.minIntervalMs - timeSinceLastRequest);
  }
}

/**
 * Singleton rate limiter instance for Nominatim API (min 1s between requests).
 */
export const nominatimRateLimiter = new RateLimiter(1000);
